import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import InquiryPops from "../../components/InquiryPops";
import Mastertemplate from "./Mastertemplate";

import {
  Box,
  Button,
  CssBaseline,
  AppBar,
  Toolbar,
  Drawer,
  Divider,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";

const OPEN_DEAL_STATUSES = ["New", "Contacted", "Offered", "Invoiced"];

function formatShortDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function TaskTodoMobile(props) {
  const drawerWidth = 240;
  const navItems = [
    { name: "Calendar", path: "/CalendarMobile" },
    { name: "Confirm Signage", path: "/ConfirmSignatureMobile" },
  ];

  const { window } = props;
  const navigate = useNavigate();
  const nowstate = useSelector((state) => state);

  const isOwner = nowstate.user.role === "owner";
  const user = nowstate?.user?.sales || "";

  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const filteredNavItems = navItems.filter((item) => {
    if (item.name === "Confirm Signage" && !isOwner) return false;
    return true;
  });

  const handleNavigation = (path) => {
    navigate(path);
    handleDrawerToggle();
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Zanio
      </Typography>
      <Divider />
      <List>
        {filteredNavItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.path)}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const [newTasks, setNewTasks] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  const [noteDrafts, setNoteDrafts] = useState({});

  const decorateTasks = useCallback((list = []) => {
    return list.map((t) => ({
      ...t,
      handled: false,
      handledCategory: null,
    }));
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setLoadError("");

      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tasktodo`,
        { params: { user } }
      );

      const data = resp.data || {};

      setNewTasks(decorateTasks(data.newTasks || []));
      setTodayTasks(decorateTasks(data.todayTasks || []));
    } catch (err) {
      console.error("Error fetching tasktodo:", err);
      setLoadError("Gagal memuat daftar tugas.");
    } finally {
      setLoading(false);
    }
  }, [user, decorateTasks]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleDraftChange = useCallback((taskId, value) => {
    if (!taskId) return;
    setNoteDrafts((prev) => ({
      ...prev,
      [taskId]: value,
    }));
  }, []);

  const handleRowClick = (task, section) => {
    if (!task?.inquiry) {
      console.warn("Task tanpa inquiry:", task);
      return;
    }

    setSelectedTask({ ...task, section });

    setSelectedInquiry({
      ...task.inquiry,
      taskId: task._id,
      taskTitle: task.title || "",
      taskDueAt: task.dueAt || null,
      Sales: task.inquiry?.Sales ?? task?.Sales ?? user,
      deal: task.deal || null,
      currentDeal: task.deal || null,
      activities: task.inquiry?.activities || [],
    });

    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedTask(null);
    setSelectedInquiry(null);
  };

  const handleInquirySaved = (oldInquiry, payload, serverData) => {
    if (!selectedTask) return;

    const updatedInquiryFromServer = serverData?.inquiry || {};
    const updatedDealFromServer = serverData?.deal || {};
    const newActivity = serverData?.activity || null;

    const finalDealStatus =
      updatedDealFromServer?.status || payload?.dealStatus || "New";

    const finalDealScore =
      updatedDealFromServer?.score ??
      payload?.dealScore ??
      selectedTask?.deal?.score ??
      "";

    const handledCategory = OPEN_DEAL_STATUSES.includes(finalDealStatus)
      ? "open"
      : "closed";

    const updateList = (setter) => {
      setter((prev) =>
        prev.map((t) => {
          if (t._id !== selectedTask._id) return t;

          const prevActivities = t?.inquiry?.activities || [];
          const nextActivities = newActivity
            ? [newActivity, ...prevActivities]
            : prevActivities;

          return {
            ...t,
            handled: true,
            handledCategory,
            inquiry: {
              ...(t.inquiry || {}),
              ...(updatedInquiryFromServer || {}),
              activities: nextActivities,
            },
            deal: {
              ...(t.deal || {}),
              ...(updatedDealFromServer || {}),
              status: finalDealStatus,
              score: finalDealScore,
            },
          };
        })
      );
    };

    if (selectedTask.section === "new") {
      updateList(setNewTasks);
    } else if (selectedTask.section === "today") {
      updateList(setTodayTasks);
    }

    if (selectedTask?._id && typeof payload?.noteText === "string") {
      setNoteDrafts((prev) => ({
        ...prev,
        [selectedTask._id]: payload.noteText,
      }));
    }
  };

  const handleExportCsv = () => {
    const rows = [];

    const pushFrom = (tasks, sectionLabel) => {
      tasks.forEach((t) => {
        const inq = t.inquiry || {};
        const deal = t.deal || {};

        rows.push({
          Section: sectionLabel,
          Nama: inq.Nama || "",
          Phone: inq.Phone || "",
          PIC: inq.Sales || "",
          Notes: t.title || "",
          DueDate: t.dueAt ? formatShortDate(t.dueAt) : "",
          TaskStatus: t.status || "",
          DealStatus: deal.status || "",
          DealScore: deal.score || "",
        });
      });
    };

    pushFrom(newTasks, "New");
    pushFrom(todayTasks, "Today");

    if (rows.length === 0) {
      window.alert("Tidak ada data untuk diexport.");
      return;
    }

    const header = Object.keys(rows[0]);
    const csvLines = [
      header.join(","),
      ...rows.map((r) =>
        header
          .map((key) => {
            const cell = (r[key] ?? "").toString().replace(/"/g, '""');
            return `"${cell}"`;
          })
          .join(",")
      ),
    ];

    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", `tasktodo_${user || "sales"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getRowBackgroundColor = (task) => {
    if (!task?.handled) return "inherit";
    if (task?.handledCategory === "closed") return "#eeeeee";
    if (task?.handledCategory === "open") return "#e8f5e9";
    return "inherit";
  };

  const renderSection = (title, color, tasks, sectionKey) => (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "inline-block",
          px: 2,
          py: 0.75,
          borderRadius: 1.5,
          backgroundColor: color,
          color: "#fff",
          fontWeight: 700,
          mb: 1.5,
        }}
      >
        {title}
      </Box>

      <Paper variant="outlined">
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nama</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nomor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Tidak ada data.
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task) => {
                  const inq = task.inquiry || {};
                  return (
                    <TableRow
                      key={task._id}
                      hover
                      onClick={() => handleRowClick(task, sectionKey)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: getRowBackgroundColor(task),
                      }}
                    >
                      <TableCell>{inq.Nama || "-"}</TableCell>
                      <TableCell>{inq.Phone || "-"}</TableCell>
                      <TableCell>{task.title || "-"}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const selectedTaskDraft =
    selectedTask?._id && noteDrafts[selectedTask._id] !== undefined
      ? noteDrafts[selectedTask._id]
      : "";

  return (
    <>
      <CssBaseline />

      <AppBar component="nav" position="fixed">
        <Toolbar sx={{ backgroundColor: "#313335", color: "white" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, display: { xs: "block", sm: "block" } }}
          >
            Task To Do
          </Typography>

          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            {filteredNavItems.map((item) => (
              <Button
                key={item.name}
                sx={{ color: "#fff" }}
                onClick={() => navigate(item.path)}
              >
                {item.name}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>

      {isOwner && <Mastertemplate />}

      <Box sx={{ mt: 8, px: 1.5, pb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6">Tugas untuk: {user || "-"}</Typography>

            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Mengambil data...
                </Typography>
              </Box>
            )}

            {!!loadError && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {loadError}
              </Typography>
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={handleExportCsv}
            disabled={newTasks.length === 0 && todayTasks.length === 0}
          >
            Export
          </Button>
        </Box>

        {renderSection("New", "#e53935", newTasks, "new")}
        {renderSection("Today", "#2e7d32", todayTasks, "today")}
      </Box>

      {popupOpen && selectedInquiry && (
        <InquiryPops
          open={popupOpen}
          handleClose={handleClosePopup}
          data={selectedInquiry}
          actorId={user}
          onSave={handleInquirySaved}
          draftNote={selectedTaskDraft}
          onDraftChange={handleDraftChange}
        />
      )}
    </>
  );
}